import { QueryErrorBoundary } from '@/catalog-apps/components/QueryErrorBoundary'
import { LoadingComponent } from '@/components/ui/LoadingComponent'
import { Suspense, useCallback, useMemo, useState } from 'react'
import { SubscriptionsBreadcrumb } from '../SubscriptionBreadcrumbs'
import { TablePageContainer } from '@/components/ui/DataTable/PageTableContainer'
import { DataTable } from '@/components/ui/DataTable'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { Button } from '@/components/shadcn/button'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { isPlatformAdmin, isProjectAdmin, isProjectLeader, isProjectMember, isSuperUser } from '@/util'
import { useCurrentOrganisation } from '../useCurrentOrganisation'
import { getOrganisationRequestColumns } from './organisationRequestsTableColumns'
import { RequestAccessForm } from '../Forms/RequestAccessForm'
import { RequestProjectCreateForm } from '../Forms/RequestProjectCreateForm'
import { TicketLookupsProvider } from '../../Platform/PlatformTicketRequestsPage/TicketLookupsContext'
import { buildTicketLookupMaps } from '../../Platform/PlatformTicketRequestsPage/ticketLookups'
import { RequestDetailSheet } from './RequestDetailSheet'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest']

export function OrganisationRequestsPage() {
  return (
    <>
      <SubscriptionsBreadcrumb title="Requests" />
      <QueryErrorBoundary errorMessage="Failed to fetch requests">
        <Suspense fallback={<LoadingComponent className="flex-1" />}>
          <OrganisationRequestsPanelContent />
        </Suspense>
      </QueryErrorBoundary>
    </>
  )
}

function OrganisationRequestsPanelContent() {
  const { id: orgId } = useCurrentOrganisation()
  const { getRequestsApiGetOrganisationRequests } = useRestClient()
  const [userRole] = useSessionStorage('roles', 1)

  const { data: response } = getRequestsApiGetOrganisationRequests.useSuspenseQuery({
    query: { orgId },
  })
  const requests = response.requests
  const lookups = useMemo(() => buildTicketLookupMaps(response.lookups), [response.lookups])
  const [detailRequest, setDetailRequest] = useState<ExistingRequest>()
  const openDetails = useCallback((request: ExistingRequest) => setDetailRequest(request), [])
  const columns = useMemo(() => getOrganisationRequestColumns(openDetails), [openDetails])

  const canRequestAccess =
    isProjectMember(userRole) ||
    isProjectLeader(userRole) ||
    isProjectAdmin(userRole) ||
    isPlatformAdmin(userRole) ||
    isSuperUser(userRole)

  return (
    <TicketLookupsProvider lookups={lookups}>
      {detailRequest && (
        <RequestDetailSheet
          request={detailRequest}
          open
          onOpenChange={(open) => {
            if (!open) setDetailRequest(undefined)
          }}
          lookups={lookups}
        />
      )}
      <TablePageContainer>
        <DataTable
          columns={columns}
          data={requests ?? []}
          action={
            <div className="flex gap-2">
              {canRequestAccess && (
                <RequestAccessForm
                  sheetTitle="Request project access"
                  sheetTrigger={
                    <Button size="lg" variant="outline">
                      <IconPlusOutline className="mr-2" />
                      Project access
                    </Button>
                  }
                />
              )}
              <RequestProjectCreateForm
                sheetTitle="Request new project"
                sheetTrigger={
                  <Button size="lg" variant="outline">
                    <IconPlusOutline className="mr-2" />
                    Project create
                  </Button>
                }
              />
            </div>
          }
        />
      </TablePageContainer>
    </TicketLookupsProvider>
  )
}
